import { EyeIcon } from '@heroicons/react/24/solid'
import { observer } from 'mobx-react'
import React, { useContext } from 'react'

import useImpersonation from '@/hooks/Impersonation'
import { UserStoreContext } from '@/stores/UserStore'

/**
 * ImpersonationBanner
 *
 * WARP-567 — a persistent, non-dismissable bar shown whenever this session is viewing
 * as another user, with the way out built into it.
 *
 * It cannot be dismissed on purpose. The banner is the only thing separating "I am
 * looking at a customer's account" from "I am looking at my own", and a support person
 * who loses that distinction can misread who they are helping. Every page in an
 * impersonated session carries it.
 */
const ImpersonationBanner = observer(() => {
  const { isImpersonating, user, impersonator } = useContext(UserStoreContext)
  const { exitViewAs, loading } = useImpersonation()

  if (!isImpersonating) return null

  const viewedName =
    user?.displayName || [user?.firstName, user?.lastName].filter(Boolean).join(' ')
  // WARP-939: WPC users are reachable now, and one who has never signed in has no name
  // at all. Without this the banner reads "Viewing as a@b.com a@b.com".
  const viewedEmail = viewedName ? user?.email : null

  // Deliberately left in normal flow with no z-index. Lifting the banner above modals
  // (`fixed inset-0 z-10`) also lifts it above the nav's profile and notification
  // menus, which are `z-10`/`z-20` but sit earlier in the DOM — one z-index cannot be
  // above the first and below the second. Prompts that would strand a session are
  // suppressed at their source instead: see the terms and MFA gates.
  return (
    <div
      className="border-gold-500 bg-gold-100 flex items-center gap-3 border-b px-4 py-3 sm:px-6 lg:px-8"
      role="status"
      aria-live="polite"
    >
      <EyeIcon className="text-gold-700 h-5 w-5 flex-none" aria-hidden="true" />

      <div className="flex flex-1 flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-2">
        <span className="text-gold-900 text-sm font-semibold">
          Viewing as {viewedName || user?.email}
        </span>
        <span className="text-gold-800 text-sm">
          {viewedEmail ? `${viewedEmail} — ` : ''}this session is read only. You are signed in as{' '}
          {impersonator?.displayName || impersonator?.email}.
        </span>
      </div>

      <button
        type="button"
        onClick={exitViewAs}
        disabled={loading}
        className="border-gold-700 text-gold-900 hover:bg-gold-200 flex-none rounded-lg border px-3 py-1.5 text-sm font-semibold disabled:opacity-60"
      >
        {loading ? 'Exiting…' : 'Exit'}
      </button>
    </div>
  )
})

export default ImpersonationBanner
